import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faLocationDot,
  faRunning,
  faChalkboardTeacher,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { db } from "@lib/firebase";
import { collection, getDocs } from "firebase/firestore";

const CACHE_KEY = "cachedEvents_SectionKegiatan_v3";
const CACHE_TTL_MS = 1000 * 60 * 5;

const formatDate = (dates) => {
  if (!dates || !Array.isArray(dates) || dates.length === 0) {
    return "Date not available";
  }
  try {
    const validDates = dates
      .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
      .map((date) => new Date(date))
      .filter((date) => !isNaN(date.getTime()));

    if (validDates.length === 0) {
      return "Date not available";
    }

    validDates.sort((a, b) => a - b);

    const formattedDates = validDates.map((date) => ({
      day: date.toLocaleDateString("en-US", { day: "numeric" }),
      month: date.toLocaleDateString("en-US", { month: "long" }),
      year: date.toLocaleDateString("en-US", { year: "numeric" }),
    }));

    const uniqueDates = [];
    const seen = new Set();
    for (const date of formattedDates) {
      const dateString = `${date.day} ${date.month} ${date.year}`;
      if (!seen.has(dateString)) {
        seen.add(dateString);
        uniqueDates.push(date);
      }
    }

    if (uniqueDates.length === 1) {
      return `${uniqueDates[0].day} ${uniqueDates[0].month} ${uniqueDates[0].year}`;
    }

    return `${uniqueDates[0].day} - ${
      uniqueDates[uniqueDates.length - 1].day
    } ${uniqueDates[uniqueDates.length - 1].month} ${
      uniqueDates[uniqueDates.length - 1].year
    }`;
  } catch {
    return "Tanggal tidak tersedia";
  }
};

const SkeletonLoader = () => (
  <div className="grid md:grid-cols-3 gap-4">
    {[...Array(3)].map((_, index) => (
      <div key={index} className="bg-white rounded-2xl border border-slate-200">
        <div className="w-full h-60 bg-gray-200 animate-pulse"></div>
        <div className="p-4 space-y-4">
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    ))}
  </div>
);

const cacheEvents = (data) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (err) {}
};

const getCachedEvents = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const EventCard = React.memo(({ event, index }) => {
  const getEventIcon = (type) => {
    switch (type.toLowerCase()) {
      case "trail_running":
        return { icon: faRunning, size: "text-5xl" };
      case "seminar":
        return { icon: faChalkboardTeacher, size: "text-4xl" };
      default:
        return { icon: faCalendarAlt, size: "text-4xl" };
    }
  };

  const { icon, size } = getEventIcon(event.type);

  return (
    <motion.div
      className="bg-white rounded-2xl border border-slate-200 flex flex-col text-left relative overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.2, ease: "easeInOut" }}
      viewport={{ once: true }}
    >
      <div className="relative w-full h-60">
        <img
          src={event.image || "https://via.placeholder.com/800x400"}
          alt={event.title}
          className="w-full h-60 object-cover rounded-t-2xl"
          loading="lazy"
        />
        <div className="absolute inset-0 rounded-t-2xl flex items-center justify-center">
          <FontAwesomeIcon
            icon={icon}
            className={`text-white ${size} z-10`}
            aria-label={`Ikon untuk ${
              event.type === "trail_running" ? "Trail Running" : "Seminar"
            }`}
          />
        </div>
      </div>
      <div className="px-4 flex flex-col flex-grow text-cyan-900">
        <motion.h3
          className="text-xl font-black mt-4 text-cyan-600 uppercase"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: index * 0.4,
            ease: "easeInOut",
          }}
          viewport={{ once: true }}
        >
          {event.title}
        </motion.h3>
        <motion.p
          className="flex-grow mb-2 mt-4"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          {event.aboutEvent && event.aboutEvent.length > 150
            ? event.aboutEvent.substring(0, 150) + "..."
            : event.aboutEvent || "Description not available"}
        </motion.p>
        <ul className="text-left mt-4">
          <motion.li
            className="flex items-center mb-2"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: index * 0.5,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="mr-2 text-cyan-600"
            />
            <span>Date: {formatDate(event.dates)}</span>
          </motion.li>
          <motion.li
            className="flex items-center mb-2"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: index * 0.6,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon
              icon={faLocationDot}
              className="mr-2 text-cyan-600"
            />
            <span>{event.location || "Location not available"}</span>
          </motion.li>
          <motion.li
            className="flex items-center mb-2"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: index * 0.7,
              ease: "easeInOut",
            }}
            viewport={{ once: true }}
          >
            <div
              className={`w-2.5 h-2.5 rounded-full mr-2 ${
                event.status === "Registration Open" || event.status === "Registrasi Dibuka"
                  ? "bg-green-500"
                  : event.status === "Registration Closed" || event.status === "Registrasi Ditutup"
                  ? "bg-red-500"
                  : "bg-yellow-500"
              }`}
            ></div>
            <span>{event.status}</span>
          </motion.li>
        </ul>
        <motion.div
          className="text-center mt-auto mb-4"
          initial={{ opacity: 0, y: -15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.8, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <Link to={`/kegiatan/${event.slug}`}>
            <button className="w-full py-3 border-2 border-cyan-600 hover:bg-cyan-600 hover:text-white text-base font-semibold text-cyan-600 rounded-xl my-4">
              View Details
            </button>
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
});

export const SectionKegiatan = React.memo(() => {
  const [events, setEvents] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      const cachedEvents = getCachedEvents();
      if (cachedEvents) {
        setEvents(cachedEvents);
        setIsLoaded(true);
        return;
      }

      if (!db || typeof db !== "object" || !("app" in db)) {
        throw new Error("Firestore db not initialized or invalid");
      }

      if (typeof collection !== "function") {
        throw new Error("Firestore collection function not available");
      }

      const snapshot = await getDocs(collection(db, "semuaKegiatan"));
      const activities = snapshot.docs.map((doc) => {
        const data = doc.data();
        const dates =
          data.categories && data.categories.length > 0
            ? [
                ...new Set(data.categories.map((category) => category.date)),
              ].filter((date) => date)
            : [];
        return {
          id: doc.id,
          title: data.title || "Title not available",
          image: data.image || "",
          location: data.location || "Location not available",
          status: data.status || "Status not available",
          slug: data.slug || "",
          aboutEvent: data.aboutEvent || "Description not available",
          dates: dates,
          eventDateTimestamp:
            data.categories && data.categories.length > 0
              ? data.categories[0].eventDateTimestamp || new Date(0)
              : new Date(0),
          type: data.type || "unknown",
        };
      });

      const sortedActivities = activities
        .filter((event) => event.id !== "JOTR" && event.id !== "FUNRUN5K2026" && event.id !== "ROADTOJOTR2026" && event.id !== "ULTRA24") // Hide JOTR 2025, Fun Run 5K Cibodas, Road to JOTR 2026, and Indonesia Ultra 24
        .sort((a, b) => {
        const dateA =
          a.eventDateTimestamp instanceof Date
            ? a.eventDateTimestamp
            : new Date(a.eventDateTimestamp || 0);
        const dateB =
          b.eventDateTimestamp instanceof Date
            ? b.eventDateTimestamp
            : new Date(b.eventDateTimestamp || 0);
        return dateA - dateB;
      });

      const limitedActivities = sortedActivities.slice(0, 3);
      cacheEvents(limitedActivities);
      setEvents(limitedActivities);
      setIsLoaded(true);
    } catch (err) {
      setError("Failed to load activities: " + err.message);
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchEvents();
    return () => {
      mounted = false;
    };
  }, [fetchEvents]);

  const sortedActivities = useMemo(() => events, [events]);

  if (!isLoaded) {
    return (
      <section className="w-full py-10 bg-slate-100 text-center">
        <div className="relative z-1 container mx-auto px-4">
          <SkeletonLoader />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full py-10 bg-slate-100 text-center">
        <div className="relative z-1 container mx-auto px-4">
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full py-10 bg-slate-100 text-center">
      <motion.h2
        className="text-xl sm:text-2xl md:text-3xl font-normal uppercase mb-2 text-cyan-600"
        initial={{ opacity: 0, y: -15 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        viewport={{ once: true }}
      >
        UPCOMING
      </motion.h2>
      <motion.p
        className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-cyan-600"
        initial={{ opacity: 0, y: -15 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3, ease: "easeInOut" }}
        viewport={{ once: true }}
      >
        EVENTS
      </motion.p>
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {sortedActivities.length === 0 ? (
            <p className="text-cyan-600">
              No upcoming events at this time.
            </p>
          ) : (
            <motion.div className="grid md:grid-cols-3 gap-4 z-1 relative">
              {sortedActivities.map((event, index) => (
                <EventCard key={event.id} event={event} index={index} />
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
});
